import { AppDispatch } from '../store';
import {
  loginSuccessReducer,
  setMeReducer,
  setInitializedReducer,
  logoutReducer,
  setAuthLoading,
  setAuthError,
} from '../reducers/authReducer';
import {
  loginApi,
  getMeApi,
  changePasswordApi,
  logoutApi,
  StaffLoginResponse,
  StaffProfile,
} from '../../api/authApi';
import { setSession } from '../../utils/jwt';
import { disablePush } from '../../utils/pushNotifications';

// Login answers, shown as they are; the server's message is used for the rest
const LOGIN_ERROR_TEXT: Record<string, string> = {
  STAFF_DEACTIVATED: 'Your login has been deactivated. Ask the owner to activate it again.',
};

const storeLogin = (dispatch: AppDispatch, res: StaffLoginResponse) => {
  setSession(res.token);
  dispatch(
    loginSuccessReducer({
      token: res.token,
      expiresAt: res.expires_at,
      staff: { ...res.staff, must_change_password: res.must_change_password },
    })
  );
};

// POST /staff/login. must_change_password true: the "Set your password" screen comes next.
export const loginThunk =
  (payload: {
    data: { email: string; password: string };
    onSuccess?: (res: StaffLoginResponse) => void;
    onFail?: (err: any) => void;
  }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setAuthLoading(true));
    dispatch(setAuthError(null));

    try {
      const res = (await loginApi(payload.data)) as StaffLoginResponse;
      if (!res?.token || !res?.staff) {
        throw new Error('Login failed: token not received');
      }
      storeLogin(dispatch, res);
      dispatch(setInitializedReducer());
      if (payload.onSuccess) payload.onSuccess(res);
    } catch (error: any) {
      // 401 Invalid email or password, 423 locked for 15 minutes, 403 STAFF_DEACTIVATED
      const errorMsg =
        (error?.code && LOGIN_ERROR_TEXT[error.code]) ||
        error?.message ||
        'Invalid email or password';
      dispatch(setAuthError(errorMsg));
      dispatch(setAuthLoading(false));
      if (payload.onFail) payload.onFail(error);
    }
  };

// GET /staff/me: who is logged in, with roles (they may have changed since the login)
export const getMeThunk =
  (payload?: { onSuccess?: (data: StaffProfile) => void; onFail?: (err: any) => void }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const me = (await getMeApi()) as StaffProfile;
      if (me?.email) {
        dispatch(setMeReducer(me));
      }
      dispatch(setInitializedReducer());
      if (payload?.onSuccess) payload.onSuccess(me);
    } catch (err) {
      dispatch(setInitializedReducer());
      if (payload?.onFail) payload.onFail(err);
    }
  };

// POST /staff/change-password. The answer carries a new token: every older one has stopped working.
// 422 WEAK_PASSWORD (extra.problems), 422 SAME_PASSWORD, 400 WRONG_PASSWORD go to onFail.
export const changePasswordThunk =
  (payload: {
    data: { current_password: string; new_password: string };
    onSuccess?: (res: StaffLoginResponse) => void;
    onFail?: (err: any) => void;
  }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const res = (await changePasswordApi(payload.data)) as StaffLoginResponse;
      storeLogin(dispatch, res);
      if (payload.onSuccess) payload.onSuccess(res);
    } catch (err) {
      if (payload.onFail) payload.onFail(err);
    }
  };

// POST /staff/logout ends every token of the user on every device. The token is
// forgotten here even when the call fails (e.g. it had already expired).
export const logoutThunk =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    // This browser stops getting this login's notification pushes (needs the token, so first)
    await disablePush().catch(() => undefined);
    try {
      await logoutApi();
    } catch (e) {
      // Already ended on the server
    }
    setSession(null);
    dispatch(logoutReducer());
  };
